import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';

import { PlanSelectionModal } from './PlanSelectionModal';
import { HandlePayment } from './HandlePayment';
import { API_ENDPOINTS, APP_ORIGIN } from '../api/config';
import {
  billingCycleForPlan,
  featureForPlan,
} from '../features/onboarding/ownerPlanSelection';
import {
  apiFetch,
  createSubscription,
  createTrialSubscriptionNoCard,
  createCheckoutSession,
  createBillingPortalSession,
} from '../api/apiService';
import { useGpsContractGate } from '../hooks/useGpsContractGate';

interface Plan {
  id: string;
  price_id: string;
  unit_amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  product: {
    product_id: string;
    name: string;
    description: string;
    metadata: any;
  };
  metadata: any;
}

interface UnifiedBuyLicensesModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
  featureType?: 'hours' | 'gps' | 'combo';
  allowTrial?: boolean; // default false; trial only allowed in onboarding
}

export function UnifiedBuyLicensesModal({ visible, onDismiss, onSuccess, featureType, allowTrial = false }: UnifiedBuyLicensesModalProps) {
  const { t } = useTranslation('onboarding');
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [subscriptionId, setSubscriptionId] = useState<string | undefined>(undefined);
  const gpsContractGate = useGpsContractGate();
  const resetGpsContract = gpsContractGate.reset;
  const [purchaseError, setPurchaseError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setPaymentModalVisible(false);
      setClientSecret(null);
      setSelectedPlan(null);
      setSelectedQuantity(1);
      setSubscriptionId(undefined);
      resetGpsContract();
      setPurchaseError(null);
    }
  }, [visible, resetGpsContract]);

  // A failed payment setup must NOT tear down the whole modal (regression:
  // license-purchase-400). Keep the plan selection open and show the reason
  // inline so the owner can retry.
  const buildPurchaseErrorMessage = (resp?: { data?: any }): string => {
    if (resp?.data?.code === 'stripe_outcome_unknown') {
      return t(
        'buyLicense.retrySafe',
        'Não confirmámos o pagamento. Aguarde alguns segundos e tente novamente.',
      );
    }
    return t(
      'buyLicense.purchaseError',
      'Não foi possível iniciar o pagamento. Tente novamente.',
    );
  };

  const isHoursMonthly = (plan: Plan): boolean =>
    featureForPlan(plan) === 'hours' && billingCycleForPlan(plan) === 'monthly';

  const doProceedWithPayment = async (plan: Plan, quantity: number) => {
    setSelectedPlan(plan);
    setSelectedQuantity(quantity);
    setPurchaseError(null);
    try {
      // Check if there is an existing subscription for this price
      // If yes, open Stripe Billing Portal for upgrades/downgrades
      try {
        const checkResp = await apiFetch<{ subscription: any }>(
          `${API_ENDPOINTS.SUBSCRIPTIONS}check_subscription/`,
          { method: 'POST', body: JSON.stringify({ price_id: plan.price_id }) }
        );
        if (checkResp.ok && checkResp.data?.subscription) {
          // Open Stripe Billing Portal instead of creating new subscription
          const baseOrigin = Platform.OS === 'web' && typeof window !== 'undefined'
            ? window.location.origin
            : APP_ORIGIN;
          
          const returnUrl = `${baseOrigin}/dashboard/manage-licenses`;
          const frontendReturnUrl = Platform.OS === 'web'
            ? returnUrl
            : Linking.createURL('/dashboard/manage-licenses');
          const portalResp = await createBillingPortalSession(
            frontendReturnUrl,
            returnUrl,
            Platform.OS !== 'web'
          );
          
          if (portalResp.ok && portalResp.data.url) {
            if (Platform.OS === 'web') {
              window.location.href = portalResp.data.url;
            } else {
              await WebBrowser.openAuthSessionAsync(
                portalResp.data.url,
                frontendReturnUrl,
                undefined
              );
            }
            onDismiss();
            return;
          }
        }
      } catch (checkError) {
        console.warn('Check subscription failed, proceeding with new purchase:', checkError);
      }

      if (allowTrial && isHoursMonthly(plan)) {
        const trialResponse = await createTrialSubscriptionNoCard({
          price_id: plan.price_id,
          quantity,
        });
        if (!trialResponse.ok || !trialResponse.data?.subscription_id) {
          setPurchaseError(buildPurchaseErrorMessage(trialResponse));
          return;
        }
        onDismiss();
        onSuccess();
        return;
      }

      if (Platform.OS === 'web') {
        // WEB: Use in-app payment modal (create_subscription + Stripe Elements)
        const resp = await createSubscription({
          price_id: plan.price_id,
          quantity,
        });

        if (!resp.ok || !resp.data.client_secret) {
          // Keep the modal open with an inline, retry-able error instead of
          // silently closing the whole flow.
          setPurchaseError(buildPurchaseErrorMessage(resp));
          return;
        }

        setClientSecret(resp.data.client_secret);
        setSubscriptionId(resp.data.subscription_id);
        setPaymentModalVisible(true);
      } else {
        // MOBILE: Use Checkout Session (external Stripe Checkout)
        const baseOrigin = APP_ORIGIN;
        
        const returnUrl = `${baseOrigin}/dashboard/home`;
        const appDeepLink = Linking.createURL('/dashboard/home');

        const response = await createCheckoutSession({
          price_id: plan.price_id,
          quantity,
          return_url: returnUrl,
          frontend_return_url: appDeepLink,
          is_mobile: true,
        });

        if (!response.ok || !response.data.checkout_url) {
          setPurchaseError(buildPurchaseErrorMessage(response));
          return;
        }

        const checkoutUrl = response.data.checkout_url;
        const result = await WebBrowser.openAuthSessionAsync(
          checkoutUrl,
          appDeepLink,
          undefined
        );
        
        if (result.type === 'success' || result.type === 'dismiss') {
          onDismiss();
          onSuccess();
        }
      }
    } catch (error: any) {
      console.error('UnifiedBuyLicensesModal: Error selecting plan:', error);
      // Do not close the modal on an unexpected failure: surface a retry-able
      // error inline so the owner does not lose the whole purchase flow.
      setPurchaseError(buildPurchaseErrorMessage());
    }
  };

  const handlePlanSelected = async (plan: Plan, quantity: number) => {
    setPurchaseError(null);
    const outcome = await gpsContractGate.requestContract(plan, quantity, {
      onAccepted: () => doProceedWithPayment(plan, quantity),
    });
    if (outcome === 'load_error') {
      setPurchaseError(gpsContractGate.loadErrorMessage);
      return;
    }
    if (outcome === 'shown') return;
    await doProceedWithPayment(plan, quantity);
  };

  return (
    <>
      <PlanSelectionModal
        visible={visible && !paymentModalVisible && !gpsContractGate.visible}
        onDismiss={onDismiss}
        onPlanSelected={handlePlanSelected}
        isTrial={allowTrial}
        featureType={featureType}
        errorMessage={purchaseError}
      />
      {gpsContractGate.contractModal}
      {clientSecret && (
        <HandlePayment
          visible={paymentModalVisible}
          onDismiss={() => { setPaymentModalVisible(false); onDismiss(); }}
          clientSecret={clientSecret}
          priceId={selectedPlan?.price_id}
          quantity={selectedQuantity}
          isTrial={false}
          subscriptionId={subscriptionId}
          onSuccess={() => { setPaymentModalVisible(false); onDismiss(); onSuccess(); }}
        />
      )}
    </>
  );
}


